import React, { useEffect, useState } from 'react';
import { getBackendUrl } from '../../../config/apiConfig';
import { createApiInstance } from '../../../api';
import logger from '../../../utils/logger';
import './UserActivityLog.css';

const activityApi = createApiInstance(getBackendUrl());

const ACTION_LABELS = {
  user_login: '🔑 Logged in',
  user_logout: '🚪 Logged out',
  session_expired: '⏳ Session expired',
  password_changed: '🔒 Changed password',
  profile_viewed: '👁️ Viewed a profile',
  profile_edited: '✏️ Edited profile',
  photo_uploaded: '📷 Uploaded a photo',
  photo_deleted: '🗑️ Deleted a photo',
  profile_visibility_changed: '👀 Changed profile visibility',
  matching_criteria_updated: '🎯 Updated match criteria',
  bio_updated: '📝 Updated bio',
  contact_info_updated: '📞 Updated contact info',
  education_updated: '🎓 Updated education',
  occupation_updated: '💼 Updated occupation',
  favorite_added: '⭐ Favorited a profile',
  favorite_removed: '☆ Unfavorited a profile',
  shortlist_added: '📋 Shortlisted a profile',
  shortlist_removed: '🗂️ Removed from shortlist',
  exclude_added: '🚫 Excluded a profile',
  exclude_removed: '✅ Unblocked a profile',
  message_sent: '💬 Sent a message',
  pii_request_sent: '🔓 Requested contact access',
  pii_request_approved: '🤝 Approved a contact request',
  search_executed: '🔍 Ran a search',
  saved_search_created: '💾 Saved a search',
  saved_search_deleted: '🗑️ Deleted a saved search',
  poll_created: '🗳️ Created a poll',
  poll_voted: '🗳️ Voted in a poll',
  event_rsvp: '📅 RSVPed to an event',
  contribution_made: '💳 Made a contribution',
  invite_sent: '✉️ Sent an invitation',
  account_paused: '⏸️ Paused account',
  account_reactivated: '▶️ Reactivated account',
};

const UserActivityLog = ({ username }) => {
  const [logs, setLogs] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!username) return undefined;
    let alive = true;
    activityApi.get('/api/activity-logs/me')
      .then((res) => { if (alive) setLogs(res.data.logs || []); })
      .catch((err) => {
        logger.error('Failed to load activity log', err);
        if (alive) setError(true);
      });
    return () => { alive = false; };
  }, [username]);

  // Group repeated actions per day → { dateKey: { label, count, ts } }
  const grouped = {};
  (logs || []).forEach((log) => {
    const d = new Date(log.timestamp);
    if (Number.isNaN(d.getTime())) return;
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}|${log.action_type}`;
    if (!grouped[key]) {
      grouped[key] = {
        label: ACTION_LABELS[log.action_type] || `📌 ${String(log.action_type).replace(/_/g, ' ')}`,
        count: 0,
        ts: d,
      };
    }
    grouped[key].count += 1;
    if (d > grouped[key].ts) grouped[key].ts = d;
  });
  const rows = Object.values(grouped).sort((a, b) => b.ts - a.ts);

  const fmtDate = (d) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  return (
    <div className="ual-section">
      <h3 className="ual-title">🕘 Your Recent Activity <span className="ual-sub">last 30 days</span></h3>
      {error ? (
        <p className="ual-empty">Couldn't load activity.</p>
      ) : logs === null ? (
        <p className="ual-empty">Loading activity…</p>
      ) : logs.length === 0 ? (
        <p className="ual-empty">No activity in the last 30 days.</p>
      ) : (
        <div className="ual-table">
          <div className="ual-row ual-head">
            <span>Date &amp; Time</span>
            <span>Activity</span>
            <span>Count</span>
          </div>
          {rows.map((r, i) => (
            <div key={i} className="ual-row">
              <span className="ual-date">{fmtDate(r.ts)}</span>
              <span className="ual-action">{r.label}</span>
              <span className="ual-count">{r.count > 1 ? `×${r.count}` : '1'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserActivityLog;
